import DataTable, { TableColumn } from 'react-data-table-component'
import { useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'

import useUserProfileFetcher from '../hooks/useUserProfileFetcher'
import { UserProfileDto } from '../types/UserProfileDto'

import { fechaIsoToYYYY_MM_DD, formatDate } from '@/utils/utils'
import CardOneSearch from '@/components/CardOneSearch'
import useFilteredData from '@/hooks/useFilteredData'
import { ButtonMenuSet } from '@/components/Buttons'
import { UserState } from '@/enums/DocStates'
import { useAuth } from '@/hooks/useAuth'
import { AppPath } from '@/enums/AppPath'

const dateSort = (rowA: UserProfileDto, rowB: UserProfileDto) => {
  const today = new Date()
  const a = rowA.user.activatedAt ?? today
  const b = rowB.user.activatedAt ?? today

  if (a > b) {
    return 1
  }

  if (b > a) {
    return -1
  }

  return 0
}

const buttonCaptions = ['Activos', 'Inactivos']

const Users: React.FC = () => {
  const [clickedIndex, setClickedIndex] = useState<number>(UserState.Activo)
  const { fetchUserProfiles, errorUserProfile } = useUserProfileFetcher()
  const [array, setArray] = useState<UserProfileDto[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const { userData } = useAuth()
  const { t } = useTranslation()

  useEffect(() => {
    if (!userData) {
      return
    }

    void fetchAsync(userData?.organization?._id ?? '', clickedIndex)
  }, [clickedIndex])

  const fetchAsync = async (orgId: string, status: number) => {
    const userProfile: UserProfileDto[] = await fetchUserProfiles(orgId, status)

    setArray(userProfile)
  }

  const navigate = useNavigate()

  const columns: TableColumn<UserProfileDto>[] = [
    {
      name: 'Nombre',
      selector: (row) => `${row.user.firstname} ${row.user.lastname}`,
      sortable: true,
      wrap: true,
      grow: 1,
    },
    {
      name: 'Email',
      selector: (row) => row.profile.email,
      sortable: true,
      grow: 1,
    },
    {
      name: 'Activado',
      selector: (row) => formatDate(fechaIsoToYYYY_MM_DD(row.profile.createdAt)),
      sortFunction: dateSort,
      sortable: true,
      right: true,
      grow: 1,
    },
    {
      name: 'Cargo',
      sortable: true,
      cell: (row) => t(row.profile.job),
      grow: 1,
    },
    {
      name: 'C.I.',
      selector: (row) => row.user.nin,
      sortable: true,
      right: true,
      wrap: true,
      grow: 1,
    },
  ]

  const onRowClickedHandler = (row: UserProfileDto): void => {
    const url = `/${AppPath.Users}/view/${row.profile._id}`
    navigate(url)
  }

  // Para la busqueda
  const filtered = useFilteredData({
    array,
    filterFields: [
      (item) => item.user?.firstname ?? '',
      (item) => item.user?.lastname ?? '',
      (item) => item.profile.email ?? '',
      (item) => item.profile.job ?? '',
    ],
    searchTerm,
    allowFilter: true,
  })

  const onClearClick = () => {
    // Vacio
  }

  return (
    <div>
      <CardOneSearch
        headerText="Gestionar "
        docuType={AppPath.Users}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        onClearClick={onClearClick}
      />
      <div className="mt-2 p-2 mx-auto ">
        <ButtonMenuSet
          captions={buttonCaptions}
          clickedIndex={clickedIndex}
          handleButtonClick={(n) => setClickedIndex(n)}
        />
        <div className="mt-4">
          <DataTable
            columns={columns}
            data={filtered}
            highlightOnHover
            pointerOnHover
            dense
            onRowClicked={onRowClickedHandler}
          />
        </div>
        <div>{errorUserProfile?.message}</div>
      </div>
    </div>
  )
}
export default Users
